/* eslint-disable camelcase */
import { affiliateDataKeyTransformations, affiliateDataKeysToDelete } from './constants'
import { fromBase64 } from '../fromBase64'
import { COOKIE_AFFILIATES } from 'src/constants/cookies'
import Cookies from 'js-cookie'
import { AffiliateData } from 'src/pages/_middleware/affiliate'
import { VALID_SOURCE_VALUES } from 'src/constants/sources'

type KeysTransformations = keyof typeof affiliateDataKeyTransformations
type ValuesTransformations = Record<
  (typeof affiliateDataKeyTransformations)[KeysTransformations],
  string
>
export type AffiliateDataCompressed = Omit<AffiliateData, KeysTransformations> &
  ValuesTransformations
/**
 * Apply the keys transformation to the affiliate data object to reduce the
 * amount of data that is stored in the cookie
 * @param rawObject
 * @returns affiliate data with transformed keys
 */
export function getAffiliateInfoReduced(rawObject: AffiliateData) {
  return Object.entries(rawObject).reduce((acc, [key, value]) => {
    // if the key is in the list of keys to delete or the value is empty, we
    // don't include it in the reduced object
    if (affiliateDataKeysToDelete.includes(key) || !value) {
      return acc
    }

    // include the value with the new key if it exists, otherwise use the
    // original key to keep all the data
    const afterKey = affiliateDataKeyTransformations[key as KeysTransformations] ?? key
    return {
      ...acc,
      [afterKey]: value,
    }
  }, {} as AffiliateDataCompressed)
}

/**
 * Revert the keys transformation to the affiliate data object to restore the
 * original keys from the query params object and maintain backwards
 * compatibility with the old keys in the cookie to avoid breaking changes
 * for users with old cookies
 * @param reducedInfo
 * @returns affiliate data with restored keys
 */
export function getAffiliateInfoRestored(reducedInfo: AffiliateDataCompressed) {
  const restored = Object.entries(reducedInfo).reduce((acc, [key, value]) => {
    const transformation = Object.entries(affiliateDataKeyTransformations).find(
      ([, after]) => after === key
    )

    if (transformation) {
      const [beforeKey] = transformation
      return {
        ...acc,
        [beforeKey]: value,
      }
    }

    return {
      ...acc,
      [key]: value,
    }
  }, {}) as AffiliateData

  return {
    ...restored,
    ...(restored.aid && { id_affiliate: restored.aid }),
  }
}

/**
 * Function that receives a string and returns a parsed object
 * @param dataToParse base64url string or a encodeURIComponent string
 * @returns object
 */
export function parseAffiliateCookieValue(dataToParse: string) {
  let parsed
  try {
    parsed = JSON.parse(fromBase64(dataToParse))
  } catch (b64Error) {
    try {
      parsed = JSON.parse(dataToParse)
    } catch (jsonError) {
      parsed = {}
    }
  }

  return getAffiliateInfoRestored(parsed)
}

export function getAffiliateFromCookie() {
  return parseAffiliateCookieValue(Cookies.get(COOKIE_AFFILIATES) ?? '{}')
}

export function isValidSource(source: string) {
  return VALID_SOURCE_VALUES.includes(source)
}
